import store from 'redux/store';
import { deleteTable } from 'redux/slices/tablesSlice';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditTable, showEditTableRows } from './edit/showEditTable';

type TableEntity = Extract<ActionEntity, { type: 'table' }>;

const isAllTable = (entities: readonly ActionEntity[]): entities is readonly TableEntity[] => entities.every((entity) => entity.type === 'table');

/**
 * Action definitions for customer tables. `createTable` is scoped; `editTable`
 * / `editRows` (single-only) and `delete` are contextual. No transport for
 * tables. delete goes through the shared confirm+delete executor (replacing the
 * page's window.confirm).
 */
export const tableActionDefinitions: ActionDefinitions = [
  {
    id: 'createTable',
    scope: 'type',
    label: 'New Table',
    resolve: (ctx) => () => void showEditTable({ mode: 'create', createIn: ctx.createIn }).then(ctx.refresh),
  },
  {
    id: 'editTable',
    label: 'Edit Table',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllTable(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditTable({ mode: 'edit', id: only.id }).then(ctx.refresh);
    },
  },
  {
    id: 'editRows',
    label: 'Edit Rows',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllTable(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => showEditTableRows(only.zoneName, only.tableName);
    },
  },
  {
    id: 'delete',
    label: 'Delete Table',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllTable(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteTable({ zone_name: entity.zoneName, table_name: entity.tableName })), {
        refresh,
        confirmBody: 'Are you sure you want to delete this table?',
      });
    },
  },
];
